import React, { useState, useEffect } from 'react';
import WalletComponent from './WalletComponent';
import OfferComponent from './OfferComponent';
import TransactionsComponent from './TransactionsComponent';
import { useAuth } from '../utils/AuthContext';
import { apiCall } from '../utils/api';
import { useLocation } from 'react-router-dom';
import { getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';

const PaymentPage = () => {
    const { authState, isLoading } = useAuth();
    const safeAuthState = authState || { userId: null, accessToken: null };
    const [offerCode, setOfferCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [offers, setOffers] = useState([]);
    const [refreshTransactions, setRefreshTransactions] = useState(null);
    const [refreshBalance, setRefreshBalance] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null); // null, 'success', 'cancelled', 'error'
    const [paymentMessage, setPaymentMessage] = useState('');
    const location = useLocation();
    const productId = 2; // Hardcoded for QuizMaster

    // Handle Stripe return (success/cancel) - Use location.search for query params
    useEffect(() => {
        if (isLoading) return; // Early return inside effect if loading

        const handleStripeReturn = async () => {
            const urlParams = new URLSearchParams(location.search);
            const redirectStatus = urlParams.get('redirect_status');
            const sessionId = urlParams.get('session_id');

            console.log('[PaymentPage] Stripe return params:', { redirectStatus, sessionId });

            if (redirectStatus || sessionId) {
                if (redirectStatus === 'succeeded' || sessionId) {
                    setPaymentStatus('success');
                    setPaymentMessage('Payment successful! Verifying...');
                    // Verify session with backend for security
                    if (sessionId) {
                        try {
                            const data = await apiCall(`/payments/status/${sessionId}`, 'GET', null, true, null, safeAuthState.accessToken);
                            if (data.message) {
                                setPaymentMessage(data.message);
                            }
                            // Trigger refreshes
                            if (refreshTransactions?.current) {
                                refreshTransactions.current();
                            }
                            if (refreshBalance?.current) {
                                refreshBalance.current();
                            }
                        } catch (error) {
                            console.error('[PaymentPage] Error verifying session:', error);
                            setPaymentStatus('error');
                            setPaymentMessage('Payment succeeded but verification failed. Contact support.');
                        }
                    }
                } else if (redirectStatus === 'cancelled') {
                    setPaymentStatus('cancelled');
                    setPaymentMessage('Payment cancelled. No points added.');
                } else {
                    setPaymentStatus('error');
                    setPaymentMessage('Payment error. Please try again or contact support.');
                }
                // Clear query params from URL
                window.history.replaceState({}, document.title, location.pathname);
            }
        };

        handleStripeReturn();
    }, [location, safeAuthState.accessToken, refreshBalance, refreshTransactions, isLoading]);

    // Auto-clear payment message after 5 seconds
    useEffect(() => {
        if (isLoading || !paymentStatus) return; // Early return inside effect if loading

        const timer = setTimeout(() => {
            setPaymentStatus(null);
            setPaymentMessage('');
        }, 5000);
        return () => clearTimeout(timer);
    }, [paymentStatus, paymentMessage, isLoading]);

    // Early return for loading
    if (isLoading) {
        return (
            <div className="payment-page">
                <h2 className="payment-header">Account & Points</h2>
                <p className="loading-text">Loading authentication state...</p>
            </div>
        );
    }

    // Simplified retryRefresh - single attempt with delay
    const retryRefresh = async (refreshFn) => {
        if (!refreshFn?.current) {
            console.warn('[PaymentPage] Refresh function not available');
            return;
        }
        try {
            await refreshFn.current();
        } catch (error) {
            console.error('[PaymentPage] Refresh failed:', error);
        }
    };

    const handleOfferCodeSubmit = async () => {
        if (!safeAuthState.userId) {
            setCodeError('Please sign in to redeem an offer.');
            return;
        }
        const upperOfferCode = offerCode.toUpperCase();
        const matchingOffer = offers.find((offer) => {
            const codeValue = typeof offer.code === 'string' ? offer.code.toUpperCase() : '';
            const tags = Array.isArray(offer.tags) ? offer.tags : [];
            return codeValue === upperOfferCode && tags.includes('points');
        });
        if (matchingOffer) {
            const now = Date.now();
            const startsAt = getUtcTimestamp(matchingOffer.starts_at);
            const endsAt = getUtcTimestamp(matchingOffer.ends_at);
            const isAfterStart = startsAt === null || now >= startsAt;
            const isBeforeEnd = endsAt === null || now <= endsAt;
            if (isAfterStart && isBeforeEnd) {
                try {
                    setIsProcessing(true);
                    setCodeError('');
                    const data = await apiCall(`/payments/redeem/${upperOfferCode}?product_id=${productId}`, 'POST', null, true, null, safeAuthState.accessToken);
                    console.log('[PaymentPage] API response for code:', data);
                    if (data.url) {
                        window.location.href = data.url; // Full redirect to Stripe
                    } else if (data.message) {
                        setPaymentStatus('success');
                        setPaymentMessage(data.message);
                        await retryRefresh(refreshTransactions);
                        await retryRefresh(refreshBalance);
                    } else {
                        throw new Error('Invalid response from server');
                    }
                } catch (error) {
                    console.error('[PaymentPage] Error in handleOfferCodeSubmit:', {
                        status: error.status,
                        message: error.message,
                        detail: error.detail,
                    });
                    if (error.status === 400 || error.status === 409) {
                        setCodeError(error.detail || 'Promo code already applied or invalid.');
                    } else if (error.status === 401) {
                        setCodeError('Authentication failed. Please sign in again.');
                    } else {
                        setCodeError(error.detail || error.message || 'An unexpected error occurred. Please try again.');
                    }
                } finally {
                    setIsProcessing(false);
                }
            } else {
                setCodeError('This offer is not currently active.');
            }
        } else {
            setCodeError('Invalid offer code or not a points promo.');
        }
    };

    const handleOfferClick = async (code, tags) => {
        if (!safeAuthState.userId) {
            alert('Please sign in to replenish points.');
            return;
        }
        if (tags.includes('standard') || tags.includes('dollar')) {
            try {
                setIsProcessing(true);
                const data = await apiCall(`/payments/replenish/${code}?product_id=${productId}`, 'POST', null, true, null, safeAuthState.accessToken);
                console.log('[PaymentPage] API response for offer:', data);
                if (data.url) {
                    window.location.href = data.url; // Full redirect to Stripe
                } else if (data.message) {
                    setPaymentStatus('success');
                    setPaymentMessage(data.message);
                    await retryRefresh(refreshTransactions);
                    await retryRefresh(refreshBalance);
                } else {
                    throw new Error('Invalid response from server');
                }
            } catch (error) {
                console.error('[PaymentPage] Error in handleOfferClick:', error);
                setPaymentMessage(`Error replenishing points: ${error.message}`);
                setPaymentStatus('error');
            } finally {
                setIsProcessing(false);
            }
        }
    };

    return (
        <div className="payment-page">
            {isProcessing && (
                <div className="processing-indicator">
                    <p>Processing... Please wait.</p>
                </div>
            )}
            {paymentStatus && (
                <div className={`payment-message ${paymentStatus}`}>
                    <p>{paymentMessage}</p>
                </div>
            )}
            <div className="payment-section">
                <h6 className="offer-title">Ace your quizzes with the top test preparation platform</h6>
                <div className="offer-section">
                    <OfferComponent
                        userId={safeAuthState.userId}
                        accessToken={safeAuthState.accessToken}
                        onOfferClick={handleOfferClick}
                        onOffersFetched={setOffers}
                        isProcessing={isProcessing}
                    >
                        {safeAuthState.userId && (
                            <div className="offer-code-input">
                                <h6 className="offer-code-title">Enter Promo Code</h6>
                                <div className="offer-code-controls">
                                    <input
                                        type="text"
                                        value={offerCode}
                                        onChange={(e) => setOfferCode(e.target.value)}
                                        placeholder="Enter offer code"
                                        className="offer-input"
                                        disabled={isProcessing}
                                    />
                                    <button className="submit-button" onClick={handleOfferCodeSubmit} disabled={isProcessing}>
                                        Submit
                                    </button>
                                </div>
                                {codeError && <p className="error-text">{codeError}</p>}
                            </div>
                        )}
                    </OfferComponent>
                </div>
            </div>
            <div className="payment-section wallet-transactions-section">
                <h6 className="wallet-title">Wallet</h6>
                <div className="wallet-transactions-card">
                    <div className="wallet-transactions-card__wallet">
                        <WalletComponent
                            userId={safeAuthState.userId}
                            accessToken={safeAuthState.accessToken}
                            onRefresh={setRefreshBalance}
                            showTitle={false}
                        />
                    </div>
                    <div className="wallet-transactions-card__divider" aria-hidden="true" />
                    <div className="wallet-transactions-card__transactions">
                        <h6 className="wallet-subtitle">Transaction History</h6>
                        <TransactionsComponent
                            userId={safeAuthState.userId}
                            accessToken={safeAuthState.accessToken}
                            onRefresh={setRefreshTransactions}
                            showTitle={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;